import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  useTheme,
  TextField,
  Chip,
  Switch,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
} from "@mui/material";
import { Search, Clear, Edit } from "@mui/icons-material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import {
  getChallenges,
  createChallenge,
  updateChallenge,
  toggleChallengeStatus,
  getSports,
} from "service/admin/contentAdminService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import moment from "moment";

const EMPTY_FORM = {
  title: "",
  description: "",
  sports_id: "",
  cover_image: "",
  dp_image: "",
  from_date: null,
  to_Date: null,
};

const ChallengeForm = ({ open, onClose, editData, sportsList, onSaved }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editData) {
        setFormData({
          title: editData.title || "",
          description: editData.description || "",
          sports_id: editData.sports_id || "",
          cover_image: editData.cover_image || "",
          dp_image: editData.dp_image || "",
          from_date: editData.from_date ? dayjs(editData.from_date) : null,
          to_Date: editData.to_Date ? dayjs(editData.to_Date) : null,
        });
      } else {
        setFormData(EMPTY_FORM);
      }
    }
  }, [open, editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({ ...prev, is_active: e.target.checked }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...formData,
        from_date: formData.from_date ? formData.from_date.toISOString() : null,
        to_Date: formData.to_Date ? formData.to_Date.toISOString() : null,
      };
      if (editData) {
        await updateChallenge(editData.id, payload);
        toast.success("Challenge updated successfully!");
      } else {
        await createChallenge(payload);
        toast.success("Challenge created successfully!");
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          `Failed to ${editData ? "update" : "create"} challenge.`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editData ? "Edit Challenge" : "Add Challenge"}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            color="secondary"
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            color="secondary"
            multiline
            rows={3}
            fullWidth
            size="small"
          />
          <FormControl size="small" color="secondary" fullWidth>
            <InputLabel color="secondary">Sport</InputLabel>
            <Select
              name="sports_id"
              label="Sport"
              color="secondary"
              value={formData.sports_id}
              onChange={handleChange}
            >
              <MenuItem value="">None</MenuItem>
              {sportsList.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Cover Image (URL / Firebase path)"
            name="cover_image"
            value={formData.cover_image}
            onChange={handleChange}
            color="secondary"
            fullWidth
            size="small"
          />
          <TextField
            label="DP Image (URL / Firebase path)"
            name="dp_image"
            value={formData.dp_image}
            onChange={handleChange}
            color="secondary"
            fullWidth
            size="small"
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={formData.from_date}
              onChange={(val) => setFormData((prev) => ({ ...prev, from_date: val }))}
              slotProps={{ textField: { color: "secondary", size: "small", fullWidth: true } }}
            />
            <DatePicker
              label="To Date"
              value={formData.to_Date}
              onChange={(val) => setFormData((prev) => ({ ...prev, to_Date: val }))}
              slotProps={{ textField: { color: "secondary", size: "small", fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? "Saving..." : editData ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Challenges = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sportFilter, setSportFilter] = useState("all");
  const [sportsList, setSportsList] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  useEffect(() => {
    getSports()
      .then((r) => {
        const data = r.data.data;
        setSportsList(Array.isArray(data) ? data : data.data || []);
      })
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: page + 1,
        limit: pageSize,
        search: search || undefined,
        sports_id: sportFilter !== "all" ? sportFilter : undefined,
      };
      const response = await getChallenges(params);
      const data = response.data.data;
      setRows(Array.isArray(data) ? data : data.data || []);
      if (data.pagination?.total !== undefined) setRowCount(data.pagination.total);
    } catch (error) {
      toast.error("Failed to load challenges.");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, sportFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      await toggleChallengeStatus(id, newStatus);
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, is_active: newStatus } : r))
      );
      toast.success(`Challenge ${newStatus ? "activated" : "deactivated"}.`);
    } catch (error) {
      toast.error("Failed to update challenge status.");
    }
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.NO",
      flex: 0.4,
      sortable: false,
      renderCell: (params) =>
        page * pageSize + params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "title",
      headerName: "TITLE",
      flex: 1.2,
    },
    {
      field: "sport_title",
      headerName: "SPORT",
      flex: 1,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "from_date",
      headerName: "FROM DATE",
      flex: 0.9,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD MMM YYYY") : "-",
    },
    {
      field: "to_Date",
      headerName: "TO DATE",
      flex: 0.9,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD MMM YYYY") : "-",
    },
    {
      field: "is_active",
      headerName: "ACTIVE",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Yes" : "No"}
          size="small"
          color={params.value ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            color="secondary"
            onClick={() => {
              setEditData(params.row);
              setFormOpen(true);
            }}
            title="Edit"
          >
            <Edit fontSize="small" />
          </IconButton>
          <Switch
            color="secondary"
            size="small"
            checked={!!params.row.is_active}
            onChange={() =>
              handleToggleStatus(params.row.id, !!params.row.is_active)
            }
          />
        </Box>
      ),
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <FlexBetween mb={1}>
          <Header title="CHALLENGES" subTitle="Manage sport challenges" />
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddCircleIcon />}
            onClick={() => {
              setEditData(null);
              setFormOpen(true);
            }}
          >
            Add Challenge
          </Button>
        </FlexBetween>

        <Box mt="1rem" mb="0.5rem" display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search by name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            color="secondary"
            sx={{ width: 240 }}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton onClick={handleSearch} color="secondary">
            <Search />
          </IconButton>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel color="secondary">Sport</InputLabel>
            <Select
              color="secondary"
              label="Sport"
              value={sportFilter}
              onChange={(e) => {
                setSportFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">All Sports</MenuItem>
              {sportsList.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          mt="10px"
          height="65vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <DataGrid
            loading={isLoading}
            getRowId={(row) => row.id}
            rows={rows}
            columns={columns}
            rowCount={rowCount || rows.length}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode={rowCount > 0 ? "server" : "client"}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
            }}
            components={{ Toolbar: DataGridCustomToolbar }}
            sx={{
              "& .MuiCircularProgress-circle": {
                color: theme.palette.secondary[300],
              },
            }}
          />
        </Box>
      </Box>

      <ChallengeForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        editData={editData}
        sportsList={sportsList}
        onSaved={fetchData}
      />
    </>
  );
};

export default Challenges;
